// Plan the rewrite of ONE open query-tab buffer after a DB-object rename.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § NAV1 (amended 2026-08-23).
//
// Before the amendment a rename left every open tab pointing at a name that no
// longer exists and merely raised a courtesy banner. That is strictly worse
// than editing the buffer, so the applied rename now rewrites the references
// it can prove — and reports the ones it deliberately did not touch instead of
// silently skipping them.
//
// Occurrences are identifier tokens from the resolver (NAV10 — never a text
// search), gated the same two-layer way SED-7 gates CTE rename:
//
//   layer 1  position — `relationSlotKind` says the token sits where only a
//                       relation reference can sit (`FROM x`, `JOIN x`,
//                       `INSERT INTO x`, `UPDATE x`, `x.col`, …).
//   layer 2  identity — the engine-aware `matchRelationRef` /
//                       `relationIdentitiesEqual` comparators the DB-side
//                       planner already uses decide whether the reference
//                       denotes the renamed object.
//
// Alias occurrences are never rewritten: renaming table `users` leaves
// `FROM users u` → `FROM customers u`, and `u.email` untouched, because the
// alias itself did not change.
//
// A third gate applies to a buffer whose tab is bound to ANOTHER database
// (`crossDatabase`, set by `renamePostApplyTabPass`): only a reference that
// SPELLS the renamed object's database denotes it there.

import type { DatabaseEngineId } from '@/core/engines';

import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';
import { computeCteBindingsForRange } from '../../sql-resolver/scope/cteScope';
import {
  RELATION_INTRODUCERS_WITH_DML,
  relationSlotKind,
} from '../../sql-resolver/scope/relationSlot';
import { mssqlDatabaseNamesCaseSensitive } from '../mssqlCollationProbe';
import {
  effectiveDatabaseFor,
  matchRelationRef,
  relationIdentityContextFor,
  type RelationIdentityContext,
} from '../relationIdentity';
import type { QualifiedName, RenameDestination, RenameKind } from '../types';

import {
  forEachIdentifierChain,
  qualifierRelationOf,
  type IdentifierChain,
} from './bufferIdentifierScan';
import type { BufferEdit } from './bufferRenamePlan';
import {
  buildBufferContext,
  foldFor,
  parseOptionsForEngine,
  resolveAliasBinding,
  type BufferContext,
} from './bufferRenameTarget';
import { matchColumnOccurrence } from './tabRewritePlanner.column';
import type { OccurrenceResult } from './tabRewriteOccurrence';
import { renderCatalogToken } from './terminalTokenEdit';

export type TabRewriteRequest = {
  readonly sql: string;
  readonly engineId: DatabaseEngineId;
  readonly kind: RenameKind;
  readonly fromIdentifier: QualifiedName;
  /** The destination the PLAN settled — the decoded new name, the delimiters
   *  its ALTER writes, and the spelling the catalog now holds. Carried, never
   *  re-derived here: this surface used to predict the catalog spelling from an
   *  identifier of its own, which is how it came to disagree with the schema
   *  ALTER, with the persisted-body rewrite, and (four times in three review
   *  rounds) with the undo. `renameDestination.ts` owns the derivation. */
  readonly destination: RenameDestination;
  /** Required for `kind: 'column'` — only refs bound to this relation rewrite. */
  readonly containingTable?: QualifiedName;
  readonly mysqlLowerCaseTableNames?: number;
  readonly mssqlCatalogCaseSensitive?: boolean;
  /** The database the rename RAN in. A reference that spells a database names
   *  the renamed object only if it is this one — without it the comparator had
   *  nothing to contradict `B.dbo.users` with, treated the absent target
   *  database as a wildcard, and rewrote a same-named table in another database
   *  from the tab bound to this one. The same effective-database rule the
   *  persisted-body planner already applies (`renameReferenceCollection.ts`
   *  passes its scope database to the same {@link effectiveDatabaseFor}), so
   *  the two halves of one rename draw one line. Absent / empty → no database
   *  context, and the check is skipped exactly as before. */
  readonly database?: string;
  /** The connection this buffer's tab belongs to. MSSQL only: read to answer
   *  whether the SERVER (instance) collation tells the database `Sales` from
   *  `sales` — the same probe `rename/renameReferenceCollection.ts` reads by connection id
   *  (`mssqlCollationProbe.ts`'s `mssqlDatabaseNamesCaseSensitive`). Absent →
   *  the database-name compare stays case-insensitive, the safe over-inclusive
   *  default. */
  readonly connectionId?: string;
  /** This buffer belongs to a tab bound to a DIFFERENT database than the one
   *  the rename ran in. A reference there denotes the renamed object only if it
   *  SPELLS that database (`FROM B.dbo.users` typed in an `A` tab); anything
   *  that leaves the database implicit names the TAB's own object. See
   *  {@link matchRelationOccurrence}. */
  readonly crossDatabase?: boolean;
};

export type TabRewritePlan = {
  /** `parse-failed` — the buffer could not be parsed, nothing was rewritten.
   *  `rewritten`    — at least one reference was rewritten.
   *  `no-match`     — the buffer parsed but holds no provable reference. */
  readonly status: 'rewritten' | 'no-match' | 'parse-failed';
  readonly edits: readonly BufferEdit[];
  /** Occurrences whose folded name matched but that the gates refused to
   *  rewrite — surfaced so the tab notice can warn instead of going quiet. */
  readonly skippedCount: number;
};

const EMPTY: TabRewritePlan = { status: 'no-match', edits: [], skippedCount: 0 };

export function planTabRewrite(request: TabRewriteRequest): TabRewritePlan {
  const options = parseOptionsForEngine(request.engineId);
  const ctx = buildBufferContext(request.sql, options);
  if (!ctx) return { status: 'parse-failed', edits: [], skippedCount: 0 };
  if (request.kind === 'column' && !request.containingTable) return EMPTY;

  const identity = relationIdentityContextFor(options.engine, {
    mysqlLowerCaseTableNames: request.mysqlLowerCaseTableNames,
    mssqlCatalogCaseSensitive: request.mssqlCatalogCaseSensitive,
    // The SERVER (instance) collation, not the (already-threaded) database's —
    // see `connectionId` above. Read the same way
    // `renameReferenceCollection.ts` does.
    mssqlDatabaseNamesCaseSensitive: request.connectionId
      ? mssqlDatabaseNamesCaseSensitive(request.connectionId)
      : undefined,
  });
  const lineIdx = ctx.lineIdx;
  const edits: BufferEdit[] = [];
  let skipped = 0;
  // The column host, resolved against the database the rename ran in. A host
  // that spells no database of its own (F2 on `dbo.users.id`) otherwise
  // compares as a wildcard against a buffer chain that DOES spell one, which is
  // how `B.dbo.users.id` came to be rewritten for a rename in `A`.
  const containingTable =
    request.kind === 'column' && request.containingTable
      ? inEffectiveDatabase(request.containingTable, request.database, identity)
      : request.containingTable;

  for (const statement of ctx.buffer.parsedDocument.statements) {
    forEachIdentifierChain(
      ctx.source,
      ctx.options,
      statement.range.startOffset,
      statement.range.endOffset,
      (chain) => {
        const hit =
          request.kind === 'column'
            ? matchColumnOccurrence({
                ctx,
                chain,
                statement,
                column: request.fromIdentifier,
                containingTable: containingTable!,
                identity,
              })
            : matchRelationOccurrence(ctx, chain, request, identity);
        if (hit === 'skip') { skipped += 1; return; }
        if (!hit) return;
        edits.push({
          range: rangeFromOffsets(lineIdx, hit.start, hit.end),
          // The SAME two arguments the persisted-body rewrite renders with
          // (`renameObjectBodyEdits.ts` → `planTerminalTokenEdit`), off the same
          // carried destination — so a view's body and an open tab cannot spell
          // one renamed column two ways.
          newText: renderCatalogToken(
            request.destination.name,
            request.destination.catalogName,
            hit.quoting,
            options.engine,
          ),
        });
      },
    );
  }

  if (edits.length === 0) return { status: 'no-match', edits: [], skippedCount: skipped };
  return { status: 'rewritten', edits: dedupeEdits(edits), skippedCount: skipped };
}

// ─── table / view ───────────────────────────────────────────────────────────

function matchRelationOccurrence(
  ctx: BufferContext,
  chain: IdentifierChain,
  request: TabRewriteRequest,
  identity: RelationIdentityContext,
): OccurrenceResult {
  const target = request.fromIdentifier;
  // Preserve the SPELLED database (not just its fold) — a case-sensitive SQL
  // Server instance needs it to tell `Sales` from `sales`; see
  // `relationIdentity.ts`'s `effectiveDatabaseFor` and
  // `identityCaseContext.ts`'s `databaseComponentEq`. The rename's own database
  // stands in when the target spells none, so `FROM B.dbo.orders` in an `A` tab
  // is a DIFFERENT relation rather than an unchecked match — see
  // {@link TabRewriteRequest.database}.
  const effectiveDb = effectiveDatabaseFor(target, request.database || undefined, identity);

  // A tab on ANOTHER database reaches the renamed object only through a
  // reference that spells the database the rename ran in. Which COMPONENT
  // carries it depends on the engine: MySQL has no schema dimension of its own
  // — a schema IS a database — so its schema slot is that component; every
  // other engine keeps a separate database part. A reference that leaves it
  // implicit is a DIFFERENT object (the tab's own), so it is dropped silently
  // rather than reported: there is nothing for the user to check.
  if (
    request.crossDatabase &&
    !(request.engineId === 'mysql'
      ? chain.qualified.foldedSchema
      : chain.qualified.foldedDatabase)
  ) {
    return null;
  }

  // Arm 1 — the chain IS the relation reference (`FROM dbo.orders`).
  const whole = matchRelationRef(chain.qualified, chain.partCount, target, effectiveDb, identity);
  if (whole && !isCteName(ctx, chain)) {
    const slot = relationSlotKind(ctx.source, chain.start, chain.end, {
      introducers: RELATION_INTRODUCERS_WITH_DML,
      disambiguateComma: true,
    });
    if (slot !== 'bare') return 'skip';
    // `ambiguous` = a bare reference against a schema-qualified target (or a
    // 4-part linked-server chain): the buffer's `orders` may be ANY schema's.
    // The DB-side planner (`renamePlanner`) excludes those unless the user
    // opts in through the preview checkbox; the tab pass has no such channel,
    // so it reports the occurrence instead of guessing.
    if (whole.confidence === 'ambiguous') return 'skip';
    return { start: chain.terminal.start, end: chain.terminal.end, quoting: chain.terminalQuoting };
  }

  // Arm 2 — the chain's HEAD is the relation and the tail is a column
  // (`orders.total`). Not relation-exclusive: a same-named table ALIAS owns
  // this slot when one is in scope, and the alias keeps its own name.
  if (chain.partCount < 2) return null;
  const headQn: QualifiedName = {
    name: chain.head.value,
    raw: chain.head.value,
    quoted: chain.head.quoted,
    foldedName: foldFor(ctx, chain.head.value, chain.head.quoted),
  };
  const head = matchRelationRef(headQn, 1, target, effectiveDb, identity);
  if (head) {
    // The head is the chain's FIRST part, so nothing precedes it to name a
    // database: in another database's tab `users.total` is that database's
    // `users`, never the renamed one.
    if (request.crossDatabase) return null;
    if (relationSlotKind(ctx.source, chain.head.start, chain.head.end) !== 'dotted-head') return null;
    if (resolveAliasBinding(ctx, chain)) return null;
    if (isCteName(ctx, chain)) return null;
    if (head.confidence === 'ambiguous') return 'skip';
    return { start: chain.head.start, end: chain.head.end, quoting: chain.head.quoting };
  }

  // Arm 3 — a 3-part chain (`dbo.orders.total`) carries the relation in its
  // MIDDLE part, which neither arm above reads. Rewriting it would mean
  // splicing an interior token whose quoting/qualification the occurrence
  // model does not carry, so it is reported as a deliberate skip rather than
  // dropped — otherwise the notice claims a clean rewrite it did not make.
  if (chain.partCount >= 3) {
    // The same read as the column arm's qualifier — one helper, so the spelled
    // schema cannot go missing on one side and not the other
    // (`bufferIdentifierScan.qualifierRelationOf`).
    const midQn = qualifierRelationOf(chain, ctx.options);
    if (midQn && matchRelationRef(midQn, 2, target, effectiveDb, identity)) {
      // In another database's tab the part BEFORE the relation is its database
      // only on a FOUR-part chain (`B.dbo.users.total`); at three parts it is
      // the schema and the database is the tab's own, i.e. another object.
      // MySQL is the exception — its schema slot IS the database, and the
      // comparison above already proved it.
      if (request.crossDatabase && request.engineId !== 'mysql' && chain.partCount < 4) return null;
      return 'skip';
    }
    // `qualifierRelationOf` also answers null for a chain whose qualifier is
    // UNREADABLE rather than absent — five parts
    // (`server.db.schema.table.column`), where `readQualifiedName` keeps the
    // last three components and the DATABASE is the one it discards. Falling
    // through to `return null` said "not a match", which is a different claim:
    // the buffer kept spelling the renamed table and the notice reported a
    // clean pass. The column arm has always reported that case
    // (`tabRewritePlanner.column.ts`'s `if (!qualifier) return 'skip'`); this
    // is the relation arm's half of the same rule.
    const unreadable = relationOfUnreadableQualifier(chain);
    if (unreadable && matchRelationRef(unreadable, 2, target, effectiveDb, identity)) return 'skip';
  }
  return null;
}

/** The relation a chain too long to read its qualifier names in that position,
 *  with its own DATABASE deliberately absent.
 *
 *  At five parts the relation and its SCHEMA are still readable — they are the
 *  qualified slots' `schema` and `database` — so a chain naming some OTHER
 *  relation stays a genuine, silent non-match instead of becoming notice noise.
 *  Only the database is gone, and an absent component is the comparator's
 *  wildcard, which is exactly right here: the caller REPORTS the occurrence
 *  rather than rewriting it, because unverifiable is not the same as matching.
 *
 *  Null for anything the chain can answer properly — `qualifierRelationOf` owns
 *  those, database included. */
function relationOfUnreadableQualifier(chain: IdentifierChain): QualifiedName | null {
  if (chain.partCount < 5) return null;
  const foldedName = chain.qualified.foldedSchema;
  if (!foldedName) return null;
  const name = chain.qualified.schema ?? foldedName;
  return {
    name,
    raw: name,
    // Chain-wide, and inert for the same reason it is in `qualifierRelationOf`:
    // every comparator over this identity reads `name` / `schema` / `folded*`.
    quoted: chain.qualified.quoted,
    foldedName,
    schema: chain.qualified.database,
    foldedSchema: chain.qualified.foldedDatabase,
  };
}

/** `name` with the database it resolves against made EXPLICIT — its own when it
 *  spells one, else the database the rename ran in. Both halves are set because
 *  `databaseComponentEq` decides a SQL Server `_CS_` instance on the spelled
 *  one and everything else on the fold. Unchanged when neither is known: the
 *  database check then stays skipped, which is the documented over-inclusive
 *  default for a rename with no database context. */
function inEffectiveDatabase(
  name: QualifiedName,
  database: string | undefined,
  identity: RelationIdentityContext,
): QualifiedName {
  const effective = effectiveDatabaseFor(name, database || undefined, identity);
  if (!effective) return name;
  return { ...name, database: effective.spelled, foldedDatabase: effective.folded };
}

/** A CTE shadows a same-named catalog relation inside its statement — renaming
 *  the table must not touch `WITH orders AS (…) … FROM orders`. */
function isCteName(ctx: BufferContext, chain: IdentifierChain): boolean {
  const stmt = ctx.buffer.parsedDocument.statements.find(
    (s) => chain.start >= s.range.startOffset && chain.start <= s.range.endOffset,
  );
  if (!stmt) return false;
  const folded = foldFor(ctx, chain.head.value, chain.head.quoted);
  return computeCteBindingsForRange(ctx.buffer, stmt.range.startOffset, stmt.range.endOffset)
    .some((c) => c.name.foldedName === folded);
}

function dedupeEdits(edits: readonly BufferEdit[]): readonly BufferEdit[] {
  const seen = new Set<number>();
  const out: BufferEdit[] = [];
  for (const e of edits) {
    if (seen.has(e.range.startOffset)) continue;
    seen.add(e.range.startOffset);
    out.push(e);
  }
  return out;
}
